import classNames from 'classnames'
import * as React from 'react'
import { Bar as RechartsBar, BarProps as RechartsBarProps, Rectangle } from 'recharts'
import { BarRectangleItem } from 'recharts/types/cartesian/Bar'
import { ActiveShape } from 'recharts/types/util/types'

import { ChartWrapperContext } from './ChartWrapper'
import { CHART_ITEM_COLORS, ChartItemColorType } from './constants'

interface ColorProps<ValueType, PayloadType> {
  color?: ChartItemColorType | ((value: ValueType | null, payload: PayloadType) => ChartItemColorType)
}

type BarInnerProps<ValueType, PayloadType> = ColorProps<ValueType, PayloadType> &
  Omit<BarRectangleItem, 'color'> & {
    payload?: PayloadType
  }

export function BarInner<ValueType, PayloadType>({ color = 'blue', ...props }: BarInnerProps<ValueType, PayloadType>) {
  const colorResult = typeof color === 'function' ? color(props.value as ValueType, props.payload!) : color
  const barColor = CHART_ITEM_COLORS[colorResult] ?? CHART_ITEM_COLORS.blue

  return <Rectangle {...props} className={classNames(props.className, 'fill-current', barColor)} />
}

type BarProps<ValueType, PayloadType> = ColorProps<ValueType, PayloadType> &
  React.PropsWithoutRef<Omit<React.ComponentProps<typeof RechartsBar>, 'color'>>

export class Bar<ValueType extends number | number[], PayloadType = any> extends React.Component<
  BarProps<ValueType, PayloadType>
> {
  static getComposedData = RechartsBar.getComposedData
  static displayName = RechartsBar.displayName
  static contextType = ChartWrapperContext
  context!: React.ContextType<typeof ChartWrapperContext>

  static defaultProps = {
    ...RechartsBar.defaultProps,
    radius: [2, 2, 0, 0],
    activeBar: false,
    isAnimationActive: false,
  }

  render() {
    const { setActiveKey } = this.context
    const { color, shape = BarInner, ...props } = this.props

    return (
      <RechartsBar
        {...props}
        fill={null as any}
        shape={
          shape
            ? (React.createElement(shape as React.FC<BarInnerProps<ValueType, PayloadType>>, {
                color,
              }) as ActiveShape<RechartsBarProps, SVGPathElement>)
            : undefined
        }
        onMouseEnter={() => {
          setActiveKey(props.dataKey as string)
        }}
        onMouseLeave={() => {
          setActiveKey(null)
        }}
      />
    )
  }
}
